import { useApolloClient, useMutation } from '@apollo/client';
import type { UseEditUser } from '@openx/components-administration/lib/utils/context/UserHookTypes.ts';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../../../components/AuthContext/context.ts';
import {
  UpdateUserDocument,
  type UpdateUserMutationVariables,
} from '../../../../graphql/mutation/users/UpdateUser.generated.tsx';

export const useEditUser: UseEditUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();

  const goBackPath = location.state?.settingsGoBackPath || '/users';
  const sessionUserId = session?.id;

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const apolloClient = useApolloClient();

  const [updateUserMutation, { loading }] = useMutation(UpdateUserDocument, {
    awaitRefetchQueries: true,
    onCompleted: ({ user }) => {
      apolloClient.cache.evict({ fieldName: 'user', id: 'ROOT_QUERY' });
      sessionUserId === user?.id && apolloClient.cache.evict({ fieldName: 'getSession', id: 'ROOT_QUERY' });
      apolloClient.cache.gc();

      enqueueSnackbar(t('User edited successfully'), { variant: 'success' });
      navigate(goBackPath);
    },
    onError: ({ graphQLErrors }) => {
      const defaultErrorMessage = t('Failed to edit user');
      const message = graphQLErrors[0]?.message;

      enqueueSnackbar(message ?? defaultErrorMessage, { variant: 'error' });
    },
    refetchQueries: ['GetUsersList', 'GetUser'],
  });

  const editUser = useCallback(
    async (formData: UpdateUserMutationVariables) => {
      await updateUserMutation({
        variables: formData,
      });
    },
    [updateUserMutation]
  );

  return { editUser, loading };
};
